import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FieldScheduleEntry,
  DaySchedule,
  getWeekStart,
  CreateScheduleEntryRequest
} from '../../types/schedule';
import {
  getFieldWeekSchedule,
  getMainRefereeWeekSchedule,
  createFieldScheduleEntry,
  updateFieldScheduleEntry,
  deleteFieldScheduleEntry,
  createMainRefereeScheduleEntry,
  updateMainRefereeScheduleEntry,
  deleteMainRefereeScheduleEntry
} from '../../services/scheduleService';
import { FieldNewsDto } from '../../services/fieldNewsService';
import ScheduleEntryCard from './ScheduleEntryCard';
import ScheduleEntryModal from './ScheduleEntryModal';
import ScheduleEntryViewModal from './ScheduleEntryViewModal';

const CalendarContainer = styled.div`
  background: var(--legacy-card-bg, #0d1b2a);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const WeekTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #00d4ff;
  font-family: 'Orbitron', sans-serif;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NavButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TodayButton = styled(NavButton)`
  background: #00d4ff;
  color: #000;
  font-weight: 600;

  &:hover {
    background: #00a8cc;
  }
`;

const AddButton = styled.button`
  background: #4CAF50;
  border: none;
  color: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  margin-left: 16px;

  &:hover {
    background: #43a047;
  }
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: 400px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DayColumn = styled.div<{ $isToday: boolean }>`
  border-right: 1px solid rgba(0, 212, 255, 0.1);
  min-height: 300px;

  &:last-child {
    border-right: none;
  }

  ${props => props.$isToday && `
    background: rgba(0, 212, 255, 0.05);
  `}

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid rgba(0, 212, 255, 0.1);
    min-height: auto;
  }
`;

const DayHeader = styled.div<{ $isToday: boolean }>`
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
  background: ${props => props.$isToday ? 'rgba(0, 212, 255, 0.15)' : 'rgba(0, 0, 0, 0.2)'};
`;

const DayName = styled.div`
  font-size: 11px;
  color: #8bb8d8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const DayNumber = styled.div<{ $isToday: boolean }>`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.$isToday ? '#00d4ff' : '#fff'};
  font-family: 'Orbitron', sans-serif;
`;

const DayMonth = styled.div`
  font-size: 10px;
  color: #8bb8d8;
  margin-top: 2px;
`;

const DayContent = styled.div`
  padding: 8px;
  min-height: 200px;
`;

const EmptyDay = styled.div`
  color: #666;
  font-size: 12px;
  text-align: center;
  padding: 20px 10px;
`;

const AddDayButton = styled.button`
  width: 100%;
  padding: 8px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px dashed rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #00d4ff;
  font-size: 12px;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8bb8d8;
`;

interface RefereeOption {
  id: string;
  name: string;
}

interface WeeklyCalendarProps {
  fieldId?: string;
  useMainRefereeApi?: boolean;
  canEdit?: boolean;
  referees?: RefereeOption[];
  onNewsClick?: (news: FieldNewsDto) => void;
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  fieldId,
  useMainRefereeApi = false,
  canEdit = false,
  referees = [],
  onNewsClick
}) => {
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [entries, setEntries] = useState<FieldScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<FieldScheduleEntry | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      let data: FieldScheduleEntry[];
      if (useMainRefereeApi) {
        data = await getMainRefereeWeekSchedule(weekStart);
      } else if (fieldId) {
        data = await getFieldWeekSchedule(fieldId, weekStart);
      } else {
        data = [];
      }
      setEntries(data);
    } catch (error) {
      console.error('Error loading schedule:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [fieldId, useMainRefereeApi, weekStart]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const getDaySchedules = (): DaySchedule[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: DaySchedule[] = [];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      date.setHours(0, 0, 0, 0);

      const dayEntries = entries.filter(entry => {
        // Парсим дату без конвертации таймзоны (берем только YYYY-MM-DD часть)
        const dateStr = entry.date.split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);
        const entryDate = new Date(year, month - 1, day);
        entryDate.setHours(0, 0, 0, 0);
        return entryDate.getTime() === date.getTime();
      });

      days.push({
        date,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        monthName: monthNames[date.getMonth()],
        entries: dayEntries,
        isToday: date.getTime() === today.getTime()
      });
    }

    return days;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    setWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    setWeekStart(newDate);
  };

  const goToToday = () => {
    setWeekStart(getWeekStart(new Date()));
  };

  const formatWeekRange = () => {
    const endDate = new Date(weekStart);
    endDate.setDate(endDate.getDate() + 6);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${weekStart.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}, ${weekStart.getFullYear()}`;
  };

  const handleEntryClick = (entry: FieldScheduleEntry) => {
    setSelectedEntry(entry);
    if (canEdit) {
      setSelectedDate(undefined);
      setModalOpen(true);
    } else {
      // View-only mode - open view modal
      setViewModalOpen(true);
    }
  };

  const handleAddClick = (date?: Date) => {
    setSelectedEntry(null);
    setSelectedDate(date || new Date());
    setModalOpen(true);
  };

  const handleSave = async (data: CreateScheduleEntryRequest) => {
    if (useMainRefereeApi) {
      if (selectedEntry) {
        await updateMainRefereeScheduleEntry(selectedEntry.id, data);
      } else {
        await createMainRefereeScheduleEntry(data);
      }
    } else if (fieldId) {
      if (selectedEntry) {
        await updateFieldScheduleEntry(selectedEntry.id, fieldId, data);
      } else {
        await createFieldScheduleEntry(fieldId, data);
      }
    }
    await loadSchedule();
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;

    if (useMainRefereeApi) {
      await deleteMainRefereeScheduleEntry(selectedEntry.id);
    } else if (fieldId) {
      await deleteFieldScheduleEntry(selectedEntry.id, fieldId);
    }
    await loadSchedule();
  };

  const daySchedules = getDaySchedules();

  return (
    <CalendarContainer>
      <CalendarHeader>
        <WeekTitle>{formatWeekRange()}</WeekTitle>
        <NavButtons>
          <NavButton onClick={goToPreviousWeek}>&larr; Prev</NavButton>
          <TodayButton onClick={goToToday}>Today</TodayButton>
          <NavButton onClick={goToNextWeek}>Next &rarr;</NavButton>
          {canEdit && (
            <AddButton onClick={() => handleAddClick()}>+ Add Entry</AddButton>
          )}
        </NavButtons>
      </CalendarHeader>

      {loading ? (
        <LoadingOverlay>Loading schedule...</LoadingOverlay>
      ) : (
        <WeekGrid>
          {daySchedules.map(day => (
            <DayColumn key={day.date.toISOString()} $isToday={day.isToday}>
              <DayHeader $isToday={day.isToday}>
                <DayName>{day.dayName}</DayName>
                <DayNumber $isToday={day.isToday}>{day.dayNumber}</DayNumber>
                <DayMonth>{day.monthName}</DayMonth>
              </DayHeader>
              <DayContent>
                {day.entries.length === 0 ? (
                  <EmptyDay>No entries</EmptyDay>
                ) : (
                  day.entries.map(entry => (
                    <ScheduleEntryCard
                      key={entry.id}
                      entry={entry}
                      onClick={handleEntryClick}
                    />
                  ))
                )}
                {canEdit && (
                  <AddDayButton onClick={() => handleAddClick(day.date)}>
                    + Add
                  </AddDayButton>
                )}
              </DayContent>
            </DayColumn>
          ))}
        </WeekGrid>
      )}

      <ScheduleEntryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={selectedEntry ? handleDelete : undefined}
        entry={selectedEntry}
        selectedDate={selectedDate}
        referees={referees}
      />

      <ScheduleEntryViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        entry={selectedEntry}
        onNewsClick={onNewsClick}
      />
    </CalendarContainer>
  );
};

export default WeeklyCalendar;
